import React from 'react';

import { Typography } from 'antd';
import styled from 'styled-components';

import ErrorMessage from 'components/lib/ErrorMessage';
import Loader from 'components/lib/Loader';
import { getFormattedDate } from 'helpers/dates';
import { formatNorthAmericanPhoneInput } from 'helpers/phoneNumbers';

import { EMAIL_MATCHING_SOURCES } from './constants';
import EmailMatchingClassificationMatchingSection from './EmailMatchingClassificationMatchingSection';
import EmailMatchingHtmlPreview from './EmailMatchingHtmlPreview';
import { EmailMatchingConfirmMatchPayloadType, EmailMatchingRecordType } from './types';

type Props = {
  detail: EmailMatchingRecordType | undefined;
  loading: boolean;
  error: boolean;
  onConfirmMatchValuesChange?: (values: EmailMatchingConfirmMatchPayloadType) => void;
};

const BODY_HEIGHT = 'min(700px, calc(100vh - 240px))';

const StateContainer = styled.div`
  height: ${BODY_HEIGHT};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
`;

const Layout = styled.div`
  height: ${BODY_HEIGHT};
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
`;

const LeftColumn = styled.div`
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  padding: 22px 24px 28px;
  border-right: 1px solid var(--line-gray);
  display: flex;
  flex-direction: column;
  gap: 26px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SectionTitle = styled(Typography.Text)`
  display: block;
  font-family: var(--font-bold);
  font-size: 11px;
  text-transform: uppercase;
  color: var(--text-secondary);
`;

const Divider = styled.div`
  height: 1px;
  background: var(--slate-gray-light);
`;

const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px 20px;
`;

const FullRow = styled.div`
  grid-column: 1 / -1;
`;

const FieldLabel = styled(Typography.Text)`
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin-bottom: 3px;
  font-family: var(--font-bold);
`;

const FieldValue = styled.div`
  font-size: 14px;
  overflow-wrap: anywhere;
`;

const EmailMatchingEditModalBody = ({ detail, loading, error, onConfirmMatchValuesChange }: Props) => {
  if (error && !loading) {
    return (
      <StateContainer>
        <ErrorMessage text="Failed to load email details. Please try again." active />
      </StateContainer>
    );
  }

  if (loading || !detail) {
    return (
      <StateContainer>
        <Loader active />
      </StateContainer>
    );
  }

  const isPdfArchive = detail.source === EMAIL_MATCHING_SOURCES.PDF_ARCHIVE;
  const isSentEmail = detail.source === EMAIL_MATCHING_SOURCES.SENT_EMAIL;
  const capturedFromSectionTitle = isPdfArchive ? 'Captured From PDF' : 'Email Community Contact Details';
  // A sent email's sender is the shopper, so the community-side contact is its recipient.
  const contactName = isSentEmail ? detail.recipient_name : detail.sender_name;
  const contactEmail = isSentEmail ? detail.recipient_email : detail.sender_email;
  const contactLabelPrefix = isPdfArchive ? 'Sender' : 'Contact';

  return (
    <Layout>
      <LeftColumn className="mini-scroll">
        <Section>
          <SectionTitle>{capturedFromSectionTitle}</SectionTitle>
          <TwoColumns>
            <div>
              <FieldLabel>{contactLabelPrefix} Name</FieldLabel>
              <FieldValue data-test-id="email-matching-detail-sender-name-value">{contactName || '-'}</FieldValue>
            </div>
            <div>
              <FieldLabel>{contactLabelPrefix} Email</FieldLabel>
              <FieldValue data-test-id="email-matching-detail-sender-email-value">{contactEmail || '-'}</FieldValue>
            </div>
            <div>
              <FieldLabel>{contactLabelPrefix} Phone</FieldLabel>
              <FieldValue data-test-id="email-matching-detail-sender-phone-value">
                {formatNorthAmericanPhoneInput(detail.sender_phone_number) || '-'}
              </FieldValue>
            </div>
            <div>
              <FieldLabel>Date / Time Sent</FieldLabel>
              <FieldValue data-test-id="email-matching-detail-sent-at-value">
                {detail.sent_at ? getFormattedDate(detail.sent_at, 'MMM DD, YYYY hh:mm A') : '-'}
              </FieldValue>
            </div>
            <div>
              <FieldLabel>Company Name</FieldLabel>
              <FieldValue data-test-id="email-matching-detail-company-name-value">
                {detail.company_name || '-'}
              </FieldValue>
            </div>
            <div>
              <FieldLabel>Community Name</FieldLabel>
              <FieldValue data-test-id="email-matching-detail-community-name-value">
                {detail.community_name || '-'}
              </FieldValue>
            </div>
            <FullRow>
              <FieldLabel>Community Address</FieldLabel>
              <FieldValue data-test-id="email-matching-detail-community-address-value">
                {detail.community_address || '-'}
              </FieldValue>
            </FullRow>
          </TwoColumns>
        </Section>

        <Divider />

        <EmailMatchingClassificationMatchingSection detail={detail} onValuesChange={onConfirmMatchValuesChange} />
      </LeftColumn>

      <EmailMatchingHtmlPreview detail={detail} />
    </Layout>
  );
};

export default EmailMatchingEditModalBody;
